/**
 * Buildertrend PDF seed data for 5 Methuen Road project.
 * This represents a typical NZ residential construction schedule
 * as exported from Buildertrend Gantt view.
 *
 * Each item has: title, startDate, workdays, phase hint, and optional color.
 * Management can import this via the Buildertrend Import modal.
 */

export interface BuildertrendRow {
  /** Row number from the PDF (1-based, for ordering reference) */
  rowNo: number;
  /** Schedule item title as shown in Buildertrend */
  title: string;
  /** Start date from the PDF (YYYY-MM-DD) */
  startDate: string;
  /** Working-day duration from the PDF */
  workdays: number;
  /** Construction phase for grouping and color */
  phase: string;
  /** Approximate bar color seen in the PDF */
  color: string;
  /** True if this item appears indented/sub-item in the PDF */
  isSubItem?: boolean;
  /** Parent row number if indented */
  parentRowNo?: number;
  /** Optional dependency hint — predecessor row number */
  predecessorRowNo?: number;
  /** Link type hint */
  linkTypeHint?: 'FS' | 'SS' | 'FF' | 'SF';
}

/**
 * Complete Buildertrend schedule for 5 Methuen Road.
 * Dates span March 2026 – December 2026 (approx 9-month build).
 */
export const buildertrend5MethuenSchedule: BuildertrendRow[] = [
  // ── Preliminaries ──
  { rowNo: 1, title: 'Preliminaries', startDate: '2026-03-09', workdays: 5, phase: 'Pre-Construction', color: '#6B7280' },
  { rowNo: 2, title: 'Set up site office and hoarding', startDate: '2026-03-09', workdays: 3, phase: 'Pre-Construction', color: '#6B7280', isSubItem: true, parentRowNo: 1 },
  { rowNo: 3, title: 'Site survey and pegging', startDate: '2026-03-12', workdays: 2, phase: 'Pre-Construction', color: '#6B7280', isSubItem: true, parentRowNo: 1 },

  // ── Consents & Approvals ──
  { rowNo: 4, title: 'Building Consent Approval', startDate: '2026-03-16', workdays: 20, phase: 'Pre-Construction', color: '#6B7280' },
  { rowNo: 5, title: 'CCC application lodgement', startDate: '2026-03-16', workdays: 5, phase: 'Pre-Construction', color: '#6B7280', isSubItem: true, parentRowNo: 4 },

  // ── Site Work ──
  { rowNo: 6, title: 'Site Preparation', startDate: '2026-04-13', workdays: 5, phase: 'Site Work', color: '#F59E0B' },
  { rowNo: 7, title: 'Strip topsoil and level site', startDate: '2026-04-13', workdays: 3, phase: 'Site Work', color: '#F59E0B', isSubItem: true, parentRowNo: 6 },
  { rowNo: 8, title: 'Temporary services connection', startDate: '2026-04-16', workdays: 2, phase: 'Site Work', color: '#F59E0B', isSubItem: true, parentRowNo: 6 },

  // ── Foundations ──
  { rowNo: 9, title: 'Foundations', startDate: '2026-04-20', workdays: 15, phase: 'Foundations', color: '#D97706' },
  { rowNo: 10, title: 'Excavation for footings', startDate: '2026-04-20', workdays: 3, phase: 'Foundations', color: '#D97706', isSubItem: true, parentRowNo: 9 },
  { rowNo: 11, title: 'Steel reinforcing and formwork', startDate: '2026-04-23', workdays: 5, phase: 'Foundations', color: '#D97706', isSubItem: true, parentRowNo: 9 },
  { rowNo: 12, title: 'Concrete slab pour', startDate: '2026-04-30', workdays: 1, phase: 'Foundations', color: '#D97706', isSubItem: true, parentRowNo: 9 },
  { rowNo: 13, title: 'Slab cure and protection', startDate: '2026-05-01', workdays: 5, phase: 'Foundations', color: '#D97706', isSubItem: true, parentRowNo: 9 },

  // ── Drainage & Services Rough-in ──
  { rowNo: 14, title: 'Under-slab drainage', startDate: '2026-04-23', workdays: 4, phase: 'Foundations', color: '#D97706' },
  { rowNo: 15, title: 'Pre-slab services rough-in', startDate: '2026-04-27', workdays: 3, phase: 'Foundations', color: '#D97706' },

  // ── Framing ──
  { rowNo: 16, title: 'Timber Floor Frame', startDate: '2026-05-11', workdays: 5, phase: 'Framing', color: '#E8621A' },
  { rowNo: 17, title: 'Wall framing – ground floor', startDate: '2026-05-18', workdays: 5, phase: 'Framing', color: '#E8621A' },
  { rowNo: 18, title: 'Wall framing – upper floor', startDate: '2026-05-25', workdays: 5, phase: 'Framing', color: '#E8621A' },
  { rowNo: 19, title: 'Roof truss supply and install', startDate: '2026-06-01', workdays: 5, phase: 'Roofing', color: '#EF4444' },
  { rowNo: 20, title: 'Roof sarking and membrane', startDate: '2026-06-08', workdays: 5, phase: 'Roofing', color: '#EF4444' },
  { rowNo: 21, title: 'Roof cladding – metal tray', startDate: '2026-06-15', workdays: 5, phase: 'Roofing', color: '#EF4444' },
  { rowNo: 22, title: 'Gutter and downpipe install', startDate: '2026-06-22', workdays: 3, phase: 'Roofing', color: '#EF4444' },

  // ── Cladding ──
  { rowNo: 23, title: 'External Cladding – Brick', startDate: '2026-06-29', workdays: 10, phase: 'Cladding', color: '#EC4899' },
  { rowNo: 24, title: 'External Cladding – Weatherboard', startDate: '2026-07-13', workdays: 10, phase: 'Cladding', color: '#EC4899' },
  { rowNo: 25, title: 'Soffit and fascia boards', startDate: '2026-07-27', workdays: 3, phase: 'Cladding', color: '#EC4899' },

  // ── Windows & Doors ──
  { rowNo: 26, title: 'Window and Door Supply', startDate: '2026-06-01', workdays: 15, phase: 'Windows & Doors', color: '#0891B2' },
  { rowNo: 27, title: 'Window installation', startDate: '2026-07-30', workdays: 5, phase: 'Windows & Doors', color: '#0891B2' },
  { rowNo: 28, title: 'External door installation', startDate: '2026-08-06', workdays: 3, phase: 'Windows & Doors', color: '#0891B2' },
  { rowNo: 29, title: 'Garage door supply and install', startDate: '2026-08-11', workdays: 2, phase: 'Windows & Doors', color: '#0891B2' },

  // ── Lock-up / Exterior ──
  { rowNo: 30, title: 'Lock-up stage complete', startDate: '2026-08-14', workdays: 1, phase: 'Exterior', color: '#10B981' },

  // ── Services Rough-in ──
  { rowNo: 31, title: 'Electrical rough-in', startDate: '2026-08-17', workdays: 10, phase: 'Interior', color: '#14B8A6' },
  { rowNo: 32, title: 'Plumbing rough-in', startDate: '2026-08-17', workdays: 10, phase: 'Interior', color: '#14B8A6' },
  { rowNo: 33, title: 'HVAC ducting and units', startDate: '2026-08-24', workdays: 5, phase: 'Interior', color: '#14B8A6' },
  { rowNo: 34, title: 'Data/cabling rough-in', startDate: '2026-08-31', workdays: 3, phase: 'Interior', color: '#14B8A6' },

  // ── Insulation & Gib ──
  { rowNo: 35, title: 'Insulation install – walls and ceiling', startDate: '2026-09-07', workdays: 5, phase: 'Interior', color: '#14B8A6' },
  { rowNo: 36, title: 'Gib board – ground floor', startDate: '2026-09-14', workdays: 5, phase: 'Interior', color: '#14B8A6' },
  { rowNo: 37, title: 'Gib board – upper floor', startDate: '2026-09-21', workdays: 5, phase: 'Interior', color: '#14B8A6' },
  { rowNo: 38, title: 'Gib stopping and sanding', startDate: '2026-09-28', workdays: 10, phase: 'Interior', color: '#14B8A6' },

  // ── Interior Fit-out ──
  { rowNo: 39, title: 'Kitchen cabinet install', startDate: '2026-10-12', workdays: 5, phase: 'Kitchen', color: '#059669' },
  { rowNo: 40, title: 'Kitchen benchtop and splashback', startDate: '2026-10-19', workdays: 3, phase: 'Kitchen', color: '#059669' },
  { rowNo: 41, title: 'Bathroom fit-out – Ensuite', startDate: '2026-10-22', workdays: 5, phase: 'Bathroom', color: '#059669' },
  { rowNo: 42, title: 'Bathroom fit-out – Main', startDate: '2026-10-29', workdays: 5, phase: 'Bathroom', color: '#059669' },
  { rowNo: 43, title: 'Vanity and tapware install', startDate: '2026-11-05', workdays: 3, phase: 'Bathroom', color: '#059669' },
  { rowNo: 44, title: 'Internal door and hardware install', startDate: '2026-11-05', workdays: 5, phase: 'Fit-out', color: '#059669' },
  { rowNo: 45, title: 'Wardrobe and storage install', startDate: '2026-11-12', workdays: 3, phase: 'Fit-out', color: '#059669' },
  { rowNo: 46, title: 'Skirting and architraves', startDate: '2026-11-17', workdays: 5, phase: 'Fit-out', color: '#059669' },

  // ── Flooring & Painting ──
  { rowNo: 47, title: 'Floor covering – tiles and carpet', startDate: '2026-11-24', workdays: 5, phase: 'Fit-out', color: '#059669' },
  { rowNo: 48, title: 'Interior painting – coats 1 & 2', startDate: '2026-11-24', workdays: 10, phase: 'Interior', color: '#14B8A6' },

  // ── Exterior & Landscaping ──
  { rowNo: 49, title: 'Deck construction', startDate: '2026-11-05', workdays: 5, phase: 'Exterior', color: '#10B981' },
  { rowNo: 50, title: 'Driveway and paving', startDate: '2026-11-12', workdays: 5, phase: 'Exterior', color: '#10B981' },
  { rowNo: 51, title: 'Landscaping – topsoil and lawn', startDate: '2026-11-19', workdays: 5, phase: 'Landscaping', color: '#22C55E' },
  { rowNo: 52, title: 'Fence and gate install', startDate: '2026-11-26', workdays: 3, phase: 'Landscaping', color: '#22C55E' },

  // ── Final Services & Handover ──
  { rowNo: 53, title: 'Electrical fit-off and testing', startDate: '2026-11-24', workdays: 3, phase: 'Interior', color: '#14B8A6' },
  { rowNo: 54, title: 'Plumbing fit-off and testing', startDate: '2026-11-27', workdays: 3, phase: 'Interior', color: '#14B8A6' },
  { rowNo: 55, title: 'Heat pump commissioning', startDate: '2026-11-30', workdays: 2, phase: 'Interior', color: '#14B8A6' },

  // ── Practical Completion ──
  { rowNo: 56, title: 'Pre-paint inspection', startDate: '2026-12-03', workdays: 1, phase: 'Handover', color: '#6B7280' },
  { rowNo: 57, title: 'Final defect rectification', startDate: '2026-12-04', workdays: 5, phase: 'Handover', color: '#6B7280' },
  { rowNo: 58, title: 'Final clean', startDate: '2026-12-11', workdays: 2, phase: 'Handover', color: '#6B7280' },
  { rowNo: 59, title: 'Practical Completion / Handover', startDate: '2026-12-15', workdays: 1, phase: 'Handover', color: '#6B7280' },
  { rowNo: 60, title: 'CCC issue', startDate: '2026-12-16', workdays: 5, phase: 'Handover', color: '#6B7280' },
];

/** Total workdays in the schedule */
export const totalBuildertrendWorkdays = buildertrend5MethuenSchedule.reduce(
  (sum, r) => sum + r.workdays,
  0
);

/** Date range */
export const buildertrendStartDate = buildertrend5MethuenSchedule[0]?.startDate ?? '2026-03-09';
export const buildertrendEndDate = '2026-12-22'; // approximate from last item + workdays